import { useEffect, useState } from 'react'

import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useWindowSize } from '@react-hook/window-size'
import { useDispatch, useSelector } from 'react-redux'

import { Box, Flex, Tab, TabList, Tabs } from '@chakra-ui/react'

import { IncidentType } from '@/graphql/generated/schemas'
import {
  closeSOPIncidentDrawer,
  selectSOPIncidentDrawerState,
} from '@/redux/ui/uiSlice'

import { DrawerCloseButton } from '../../components/DrawerCloseButton'
import {
  INCIDENT_DRAWER_WIDTH,
  IncidentDrawerContainer,
} from '../../components/IncidentDrawerContainer'
import { SOPChatbot } from '../../sop-chatbot'
import {
  type IncidentContextI,
  SOP_AI_ASSIST_EVENT,
} from '../../sop-chatbot/types'
import { SOPIncidentDetail } from './SOPIncidentDetail'
import { SOPIncidentTable } from './SOPIncidentTable'

export const SOP_DRAWER_WIDTH = 810
export const SOP_DRAWER_BREAKPOINT = INCIDENT_DRAWER_WIDTH + SOP_DRAWER_WIDTH

interface SOPIncidentDrawerIProps {
  incidentContext: IncidentContextI | null
}

const TAB_ASK = 0
const TAB_DOCUMENTS = 1

const tabStyle = {
  _selected: { borderColor: 'brand.100', color: 'brand.100' },
  color: 'text.secondary.light',
  fontSize: '14px',
  fontWeight: 600,
  px: 0,
}

export const SOPIncidentDrawer = ({
  incidentContext,
}: SOPIncidentDrawerIProps) => {
  const { id, isOpen } = useSelector(selectSOPIncidentDrawerState)
  const dispatch = useDispatch()
  const [width] = useWindowSize()
  const [activeTab, setActiveTab] = useState(TAB_DOCUMENTS)
  const { salesDatabaseReset: isSalesEnvironment } = useFeatureFlags()

  const shouldShowTabs =
    isSalesEnvironment &&
    incidentContext?.incident_type === IncidentType.DoorHeldOpen

  useEffect(() => {
    if (width < SOP_DRAWER_BREAKPOINT) {
      dispatch(closeSOPIncidentDrawer())
    }
  }, [width])

  // When viewing a specific SOP document, switch to documents tab
  useEffect(() => {
    if (shouldShowTabs && id !== null) {
      setActiveTab(TAB_DOCUMENTS)
    }
  }, [id, shouldShowTabs])

  // Listen for sop-ai-assist event to switch to Ask tab
  useEffect(() => {
    if (!shouldShowTabs) return
    const handler = () => setActiveTab(TAB_ASK)
    window.addEventListener(SOP_AI_ASSIST_EVENT, handler)
    return () => window.removeEventListener(SOP_AI_ASSIST_EVENT, handler)
  }, [shouldShowTabs])

  if (!isOpen) return null

  const onClose = () => dispatch(closeSOPIncidentDrawer())

  // Non-sales or non-DHO: render original documents-only view
  if (!shouldShowTabs) {
    return (
      <IncidentDrawerContainer
        dataTestid='SOPIncidentDrawerContainer:wrapper'
        width={SOP_DRAWER_WIDTH}
      >
        {id !== null ? (
          <SOPIncidentDetail id={id} />
        ) : incidentContext ? (
          <SOPIncidentTable facilityId={incidentContext.facility_id} />
        ) : null}
      </IncidentDrawerContainer>
    )
  }

  // Sales + DHO: render tabbed layout with Ask and Documents
  return (
    <IncidentDrawerContainer
      dataTestid='SOPIncidentDrawerContainer:wrapper'
      width={SOP_DRAWER_WIDTH}
    >
      <Flex direction='column' h='100%'>
        {/* Tab header with close button */}
        <Box
          alignItems='center'
          borderBottom='1px solid #D5DCE4'
          display='flex'
          flexShrink={0}
          h='65px'
          px='24px'
        >
          <Tabs flex={1} index={activeTab} onChange={setActiveTab}>
            <TabList border='none' gap={6}>
              <Tab {...tabStyle}>Ask</Tab>
              <Tab {...tabStyle}>Documents</Tab>
            </TabList>
          </Tabs>
          <DrawerCloseButton onClick={onClose} />
        </Box>

        {/* Tab content: both panels rendered, hidden via display to preserve state */}
        <Box
          display={activeTab === TAB_ASK ? 'block' : 'none'}
          flex={1}
          minH={0}
          overflow='hidden'
        >
          <SOPChatbot incidentContext={incidentContext} />
        </Box>
        <Box
          display={activeTab === TAB_DOCUMENTS ? 'block' : 'none'}
          flex={1}
          minH={0}
          overflow='auto'
        >
          {id !== null ? (
            <SOPIncidentDetail id={id} />
          ) : incidentContext ? (
            <SOPIncidentTable
              facilityId={incidentContext.facility_id}
              hideHeader
            />
          ) : null}
        </Box>
      </Flex>
    </IncidentDrawerContainer>
  )
}
